import { Left, Right, isRight, left, right } from "fp-ts/Either";
import { Draft, Producer, castDraft, produce } from "immer";
import * as t from "io-ts";
import { Json, JsonFromString } from "io-ts-types";
import { PathReporter } from "io-ts/PathReporter";
import React, { createContext, useCallback, useEffect, useState } from "react";

export type AnyObject = Record<string | number | symbol, unknown>;
export type AnyObjectOrNull = AnyObject | null;
export type AnyArray = Readonly<AnyObject[]>;
export type AnyObjOrArray = AnyObject | AnyArray;
export type AnyObjOrArrayOrNull = AnyObject | AnyArray | null;

/**
 * Base (abstract) StorageBackend class. Implementations should subclass and
 * define the rawRead and rawWrite methods.
 */
export abstract class StorageBackend<
    Value extends AnyObjOrArrayOrNull,
    EncodedValue extends Json,
> {
    protected readonly codec: t.Type<Value, EncodedValue>;
    protected readonly defaultVal: Value;

    public constructor(opts: {
        codec: t.Type<Value, EncodedValue>;
        defaultVal: Value;
    }) {
        this.codec = opts.codec;
        this.defaultVal = opts.defaultVal;
    }

    public async read(): Promise<Value> {
        const maybeSerObj = await this.rawRead();
        if (isRight(maybeSerObj)) {
            const maybeObj = this.codec.decode(
                this.deserialize(maybeSerObj.right),
            );
            if (isRight(maybeObj)) {
                return maybeObj.right;
            }
            this.reportCodecErrors(maybeObj);
        }
        return this.getDefault();
    }

    public async write(obj: Value): Promise<void> {
        const serObj = this.serialize(this.codec.encode(obj));
        await this.rawWrite(serObj);
    }

    public getDefault(): Value {
        /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
        const defaultVal: typeof this.defaultVal = JSON.parse(
            JSON.stringify(this.defaultVal),
        );
        return defaultVal;
    }

    protected abstract rawRead(): Promise<Left<null> | Right<string>>;

    protected abstract rawWrite(serObj: string): Promise<void>;

    protected serialize(obj: EncodedValue): string {
        return JsonFromString.encode(obj);
    }

    protected deserialize(serObj: string | null): Json {
        if (serObj) {
            const maybeDecoded = JsonFromString.decode(serObj);
            if (isRight(maybeDecoded)) {
                return maybeDecoded.right;
            }
            this.reportCodecErrors(maybeDecoded);
        }
        return null;
    }

    protected reportCodecErrors(result: Left<t.Errors>): void {
        const msg = PathReporter.report(result).join("\n\n");
        console.error(msg);
    }
}

export type StorageBackendOpts<
    Value extends AnyObjOrArrayOrNull,
    EncodedValue extends Json,
> = ConstructorParameters<typeof StorageBackend<Value, EncodedValue>>[0];

/**
 * Storage backend implementation which uses localStorage.
 */
export class LocalStorageBackend<
    Value extends AnyObjOrArrayOrNull,
    EncodedValue extends Json,
> extends StorageBackend<Value, EncodedValue> {
    protected readonly storageKey: string;

    public constructor(
        opts: StorageBackendOpts<Value, EncodedValue> & {
            storageKey: string;
        },
    ) {
        super(opts);
        this.storageKey = opts.storageKey;
    }

    protected rawRead(): Promise<Left<null> | Right<string>> {
        const value = localStorage.getItem(this.storageKey);
        const result = value === null ? left(null) : right(value);
        return Promise.resolve(result);
    }

    protected rawWrite(serObj: string): Promise<void> {
        localStorage.setItem(this.storageKey, serObj);
        return Promise.resolve();
    }
}

export class Repository<
    Value extends AnyObjOrArrayOrNull,
    EncodedValue extends Json,
> {
    protected readonly backend: StorageBackend<Value, EncodedValue>;
    protected readonly writeLockName: string;
    public readonly broadcastChannel: BroadcastChannel;

    constructor(opts: {
        backend: StorageBackend<Value, EncodedValue>;
        writeLockName: string;
        broadcastChannelName: string;
    }) {
        this.backend = opts.backend;
        this.writeLockName = opts.writeLockName;
        this.broadcastChannel = new BroadcastChannel(opts.broadcastChannelName);
    }

    public getDefault(): Value {
        return this.backend.getDefault();
    }

    public async get(): Promise<Value> {
        let obj = this.backend.getDefault();
        await this.withLock(async () => {
            obj = await this.backend.read();
        });
        return obj;
    }

    public async set(obj: Value): Promise<Value> {
        await this.withLock(async () => {
            await this.backend.write(obj);
        });
        this.sendDataChangeSignals();
        return obj;
    }

    public async update(recipe: Producer<Value>): Promise<Value> {
        let newObj = this.backend.getDefault();
        await this.withLock(async () => {
            const origObj = await this.backend.read();
            newObj = produce(origObj, recipe);
            await this.backend.write(newObj);
        });
        this.sendDataChangeSignals();
        return newObj;
    }

    public addDataChangeListener(fn: () => void): void {
        this.broadcastChannel.addEventListener("message", fn);
    }

    public removeDataChangeListener(fn: () => void): void {
        this.broadcastChannel.removeEventListener("message", fn);
    }

    public sendDataChangeSignals(): void {
        this.broadcastChannel.postMessage("VALUE_UPDATED");
    }

    protected async withLock(callback: () => Promise<void>): Promise<void> {
        // navigator.locks doesn't exist in NodeJS contexts, not even in JSDOM for testing.
        /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
        if (navigator.locks && navigator.locks.request) {
            await navigator.locks.request(this.writeLockName, callback);
        } else {
            await callback();
        }
    }
}

export type RepositoryOpts<
    Value extends AnyObjOrArrayOrNull,
    EncodedValue extends Json,
> = ConstructorParameters<typeof Repository<Value, EncodedValue>>[0];

/**
 * High-level, concurrency-save, persistent store for array data types.
 */
export class ArrayRepository<
    Value extends AnyObject,
    EncodedValue extends Json,
> extends Repository<Value[], EncodedValue> {
    protected readonly keyProp: keyof Value;

    public constructor(
        opts: RepositoryOpts<Value[], EncodedValue> & {
            keyProp: keyof Value;
        },
    ) {
        super(opts);
        this.keyProp = opts.keyProp;
    }

    public async list(): Promise<Value[]> {
        return this.get();
    }

    public async add(newObj: Value): Promise<Value[]> {
        return this.update((arr) => {
            const newArr = arr.filter(this.without(newObj));
            newArr.push(castDraft(newObj));
            return newArr;
        });
    }

    public async remove(rmObj: Value): Promise<Value[]> {
        return this.update((arr) => {
            return arr.filter(this.without(rmObj));
        });
    }

    protected without(omitObj: Value): (obj: Value | Draft<Value>) => boolean {
        return (obj: Value | Draft<Value>) => {
            return (obj as Value)[this.keyProp] !== omitObj[this.keyProp];
        };
    }
}

/**
 * Given a Repository and a listener function, call the listener immediately
 * upon component mount, and then again any time repository data changes.
 */
export const useRepositoryStateSubscription = <
    Value extends AnyObjOrArrayOrNull,
    EncodedValue extends Json,
>(
    repo: Repository<Value, EncodedValue>,
    listener: () => void,
): void => {
    useEffect(() => {
        // On mount, read favs from local storage and update state
        listener();

        // Subscribe to events in the broadcast channel so that favorites across
        // multiple tabs stay in-sync.
        repo.addDataChangeListener(listener);
        return () => {
            repo.removeDataChangeListener(listener);
        };
    }, [repo, listener]);
};

/**
 * Constructor function for a persistent React context for object data types.
 */
export const newPersistentObjContext = <
    Value extends AnyObjectOrNull,
    EncodedValue extends Json,
>({
    repo,
}: {
    repo: Repository<Value, EncodedValue>;
}) => {
    type ContextData = {
        value: Value;
        set: (obj: Value) => void;
        update: (recipe: Producer<Value>) => void;
    };

    const defaultVal = repo.getDefault();
    const Context = createContext<ContextData>({
        value: defaultVal,
        set: () => null,
        update: () => null,
    });

    const Provider = (props: React.PropsWithChildren) => {
        const [value, setValue] = useState<Value>(defaultVal);

        const setStateFromStorage = useCallback(() => {
            repo.get().then(setValue).catch(console.error);
        }, [repo, setValue]);

        const set: ContextData["set"] = useCallback(
            (obj) => {
                repo.set(obj).then(setValue).catch(console.error);
            },
            [repo, setValue],
        );

        const update: ContextData["update"] = useCallback(
            (recipe: Producer<Value>) => {
                repo.update(recipe).then(setValue).catch(console.error);
            },
            [repo, setValue],
        );

        // Subscribe to events in the repository
        useRepositoryStateSubscription(repo, setStateFromStorage);

        const val: ContextData = {
            value: value,
            set: set,
            update: update,
        };
        return (
            <Context.Provider value={val}>{props.children}</Context.Provider>
        );
    };

    return {
        Context,
        Provider,
    };
};

/**
 * Constructor function for a persistent React context for array data types.
 */
export const newPersistentArrayContext = <
    Value extends AnyObject,
    EncodedValue extends Json,
>({
    repo,
}: {
    repo: ArrayRepository<Value, EncodedValue>;
}) => {
    type ContextData = {
        items: Value[];
        add: (obj: Value) => void;
        remove: (fav: Value) => void;
    };

    const Context = createContext<ContextData>({
        items: [],
        add: () => null,
        remove: () => null,
    });

    const Provider = (props: React.PropsWithChildren) => {
        const [items, setItems] = useState<Value[]>([]);

        const setStateFromStorage = useCallback(() => {
            repo.list().then(setItems).catch(console.error);
        }, [repo, setItems]);

        const add: ContextData["add"] = useCallback(
            (newFav) => {
                repo.add(newFav).then(setItems).catch(console.error);
            },
            [repo, setItems],
        );

        const remove: ContextData["remove"] = useCallback(
            (fav) => {
                repo.remove(fav).then(setItems).catch(console.error);
            },
            [repo, setItems],
        );

        // Subscribe to events in the repository
        useRepositoryStateSubscription(repo, setStateFromStorage);

        const val: ContextData = {
            items: items,
            add: add,
            remove: remove,
        };
        return (
            <Context.Provider value={val}>{props.children}</Context.Provider>
        );
    };

    return {
        Context,
        Provider,
    };
};
